import React from 'react'
import { QuoteListItem } from './QuoteListItem'
import { EmptyState } from './EmptyState'
import { QuoteListSkeleton } from './Skeletons'
import type { Estimate } from '../estimates/types'

type ProjectGroup = { counsel_id: number; counsel: Estimate['counsel']; estimates: Estimate[] }

interface QuoteListProps {
  estimates: Estimate[]
  projectGroups?: ProjectGroup[]
  selectedEstimateId: number | null
  loading: boolean
  isRead: (estimateId: number) => boolean
  onSelect: (estimateId: number) => void
  onResetFilters?: () => void
  hasActiveFilters?: boolean
  compareSelection?: number[]
  compareLimitReached?: boolean
  onToggleCompare?: (estimateId: number, checked: boolean) => void
  onSendRequestForProject?: (counselId: number) => void
}

export const QuoteList: React.FC<QuoteListProps> = ({
  estimates,
  projectGroups = [],
  selectedEstimateId,
  loading,
  isRead,
  onSelect,
  onResetFilters,
  hasActiveFilters,
  compareSelection = [],
  compareLimitReached = false,
  onToggleCompare,
  onSendRequestForProject,
}) => {
  if (loading) {
    return (
      <div className="p-4">
        <QuoteListSkeleton />
      </div>
    )
  }

  if (estimates.length === 0) {
    return (
      <EmptyState
        title={hasActiveFilters ? '조건에 맞는 견적서가 없어요' : '도착한 견적서가 아직 없어요'}
        description={
          hasActiveFilters
            ? '필터를 초기화하거나 검색어를 변경해보세요.'
            : '팀/프리랜서가 견적을 제출하면 여기에서 확인할 수 있어요.'
        }
        actionLabel={hasActiveFilters ? '필터 초기화' : undefined}
        onAction={hasActiveFilters ? onResetFilters : undefined}
      />
    )
  }

  const renderCard = (estimate: Estimate) => (
    <QuoteListItem
      key={estimate.estimate_id}
      estimate={estimate}
      isSelected={selectedEstimateId === estimate.estimate_id}
      isRead={isRead(estimate.estimate_id)}
      onClick={() => onSelect(estimate.estimate_id)}
      compareSelected={compareSelection.includes(estimate.estimate_id)}
      compareDisabled={compareLimitReached}
      onToggleCompare={onToggleCompare ? (checked) => onToggleCompare(estimate.estimate_id, checked) : undefined}
    />
  )

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-4 space-y-3">
        {projectGroups.length > 0 ? (
          projectGroups.map((group) => {
            const unreadCount = group.estimates.filter((e) => !isRead(e.estimate_id)).length
            const title = group.counsel?.title || '제목 없음'
            return (
              <div key={group.counsel_id} className="space-y-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900 truncate">{title}</h2>
                  <p className="text-xs text-gray-500">
                    받은 견적 {group.estimates.length} | 미확인 {unreadCount}
                    {onSendRequestForProject && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onSendRequestForProject(group.counsel_id)
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          추가 요청
                        </button>
                      </>
                    )}
                  </p>
                </div>
                <div className="space-y-2">
                  {group.estimates.map((estimate) => renderCard(estimate))}
                </div>
              </div>
            )
          })
        ) : (
          estimates.map((estimate) => renderCard(estimate))
        )}
      </div>
    </div>
  )
}
